import { createContext, useEffect, useState } from "react";
import Client from "../tools/client";
import { defaultTheme } from "../styles/theme";

// Placeholder for future dark mode toggle
const isDarkMode = false;

const client = new Client();

export const ConfigurationContext = createContext({
  configuration: {},
  setConfiguration: () => null,
  theme: defaultTheme,
  setAppTheme: () => null,
  rectangleLogo: null,
  squareLogo: null,
  refreshConfiguration: async () => {} // exposed refresh function
});

export const ConfigurationProvider = ({ children }) => {
  const [configuration, setConfiguration] = useState({});
  const [theme, setTheme] = useState(defaultTheme);
  const [rectangleLogo, setRectangleLogo] = useState(null);
  const [squareLogo, setSquareLogo] = useState(null);

  const parseField = (field, fallback) => {
    if (typeof field === "string") {
      try {
        return JSON.parse(field);
      } catch (e) {
        console.warn("Failed to parse theme field:", e);
        return fallback;
      }
    }
    return field ?? fallback;
  };

  const applyTheme = (themeRaw) => {
    if (!themeRaw) {
      setTheme(defaultTheme);
      return;
    }

    const parsedTheme = {
      ...defaultTheme,
      ...themeRaw,
      palette: parseField(themeRaw.palette, defaultTheme.palette),
      typography: parseField(themeRaw.typography, defaultTheme.typography),
      layout: parseField(themeRaw.layout, defaultTheme.layout),
      behavior: parseField(themeRaw.behavior, defaultTheme.behavior)
    };

    const modePalette = isDarkMode
      ? parsedTheme.palette.secondary
      : parsedTheme.palette.primary;

    parsedTheme.modePalette = modePalette;

    setTheme(parsedTheme);
  };

  const refreshConfiguration = async () => {
    const setup = await client.setup();
    if (!setup.error) {
      const config = setup.configuration;
      setConfiguration(config);
      applyTheme(setup.theme);
      setRectangleLogo(config?.branding?.logo?.rectangle?.image || null);
      setSquareLogo(config?.branding?.logo?.square?.image || null);
    }
  };

  useEffect(() => {
    refreshConfiguration();
  }, []);

  return (
    <ConfigurationContext.Provider
      value={{
        configuration,
        setConfiguration,
        theme,
        setAppTheme: applyTheme,
        rectangleLogo,
        squareLogo,
        refreshConfiguration
      }}
    >
      {children}
    </ConfigurationContext.Provider>
  );
};